import { TempDemoManager } from '@/utils/tempDemoManager';
import type { DemoData, SimpleAgentConfig, SimpleDemoConfig, SimpleExamplesConfig } from '@demo-portal/shared';
import { useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';

export const useDemoConfig = (demoId: string) => {
  const [demoData, setDemoData] = useState<DemoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDemoConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, check if this is a temporary demo configuration
        if (TempDemoManager.isTemporaryId(demoId)) {
          const tempDemoData = TempDemoManager.retrieve(demoId);

          if (tempDemoData) {
            setDemoData(tempDemoData);
            console.log(`Loaded temporary demo: ${demoId}`);
            return;
          } else {
            // Temporary demo not found or expired
            console.warn(`Temporary demo not found or expired: ${demoId}`);
            throw new Error('This temporary demo has expired or could not be found. Please generate a new demo.');
          }
        }

        // Check if this is a static demo (existing demos) or a generated demo
        const staticDemoMappings: Record<string, string> = {
          'sgs': '/demos/sgs',
          'sgs-automotive-certification': '/demos/sgs',
          'givaudan': '/demos/givaudan',
          'givaudan-fragrance-development': '/demos/givaudan',
        };

        if (staticDemoMappings[demoId]) {
          // Load static demo files (existing behavior)
          const basePath = staticDemoMappings[demoId];

          const [configResponse, agentsResponse, examplesResponse] = await Promise.all([
            fetch(`${basePath}/config.json`),
            fetch(`${basePath}/agents.json`),
            fetch(`${basePath}/examples.json`),
          ]);

          if (!configResponse.ok || !agentsResponse.ok || !examplesResponse.ok) {
            throw new Error('Failed to load demo configuration files');
          }

          const [config, agents, examples] = await Promise.all([
            configResponse.json() as Promise<SimpleDemoConfig>,
            agentsResponse.json() as Promise<SimpleAgentConfig>,
            examplesResponse.json() as Promise<SimpleExamplesConfig>,
          ]);

          setDemoData({ config, agents, examples });
        } else {
          // Load generated demo configuration data from API endpoints
          const [configResult, agentsResult, examplesResult] = await Promise.all([
            apiClient.get<SimpleDemoConfig>(`/demo/${demoId}/config`),
            apiClient.get<SimpleAgentConfig>(`/demo/${demoId}/agents`),
            apiClient.get<SimpleExamplesConfig>(`/demo/${demoId}/examples`),
          ]);

          // Check for errors in any of the API calls
          if (configResult.error) {
            throw new Error(configResult.error || 'Failed to load demo configuration');
          }
          if (agentsResult.error) {
            throw new Error(agentsResult.error || 'Failed to load demo agents');
          }
          if (examplesResult.error) {
            throw new Error(examplesResult.error || 'Failed to load demo examples');
          }

          // Check for successful responses and required data
          if (configResult.status < 200 || configResult.status >= 300 || !configResult.data) {
            throw new Error('Failed to load demo configuration');
          }
          if (agentsResult.status < 200 || agentsResult.status >= 300 || !agentsResult.data) {
            throw new Error('Failed to load demo agents');
          }
          if (examplesResult.status < 200 || examplesResult.status >= 300 || !examplesResult.data) {
            throw new Error('Failed to load demo examples');
          }

          setDemoData({
            config: configResult.data,
            agents: agentsResult.data,
            examples: examplesResult.data,
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load demo configuration');
        console.error('Error loading demo config:', err);
      } finally {
        setLoading(false);
      }
    };

    if (demoId) {
      loadDemoConfig();
    }
  }, [demoId]);

  return { demoData, loading, error };
};